import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Typography, Box } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import axios from 'axios';
import api from '../api/axiosConfig';
import { useTranslation } from 'react-i18next';

/**
 * Componente reutilizable para la conversión de voz a texto.
 *
 * @param {function} onTranscription - Función callback llamada con la transcripción devuelta por la API.
 * @param {string} backendUrl - URL del servidor backend (puede obtenerse de process.env.REACT_APP_BACKEND_URL).
 */
const SpeechToTextButton = ({ onTranscription, backendUrl }) => {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);

  // Nouveau state pour gérer le compte à rebours
  const [timeLeft, setTimeLeft] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    let timerInterval;

    if (recording) {
      // Démarre le compte à rebours à 58 secondes
      setTimeLeft(29);
      timerInterval = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            // Si le temps restant arrive à 0, on arrête l'enregistrement
            stopRecording();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else {
      // Quand on arrête l'enregistrement, on réinitialise le minuteur
      setTimeLeft(0);
    }

    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [recording]);

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        audioChunksRef.current = [];
        await sendAudioForTranscription(audioBlob);
      };

      mediaRecorderRef.current.start();
      setRecording(true);
    } catch (error) {
      console.error('Error al capturar el audio:', error);
      setError(t('tecnification.audioError'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const sendAudioForTranscription = async (audioBlob) => {
    const formDataAudio = new FormData();
    formDataAudio.append('audio', audioBlob, 'grabacion.wav');
    try {
      // Note: Using axios directly with backendUrl prop for multipart/form-data upload
      const response = await axios.post(
        `${backendUrl}/api/importar/speech-to-text`,
        formDataAudio,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      const transcription = response.data.transcription;
      // Llamamos a la función onTranscription para enviar los datos al componente padre
      if (onTranscription) {
        onTranscription(transcription);
      }
    } catch (err) {
      console.error("Error al enviar el audio al servidor:", err);
      setError(t('tecnification.transcriptionError'));
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <IconButton
        color={recording ? 'error' : 'primary'}
        onClick={recording ? stopRecording : startRecording}
      >
        <MicIcon fontSize="large" />
      </IconButton>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="body2">
          {recording ? t('tecnification.recording') : t('tecnification.microphoneHint')}
        </Typography>
        {recording && (
          <Typography variant="body2" color="text.secondary">
            {t('tecnification.timeRemaining')}: {timeLeft}s
          </Typography>
        )}
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ ml: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default SpeechToTextButton;
